// Public legal pages (Privacy Policy / Terms of Service).
//
// The canonical text lives in src/content/legal/*.md and is the SAME source
// used to build the attorney-review PDFs, so the in-app pages can never drift
// from the documents under review. We render a small, controlled subset of
// Markdown (headings, paragraphs, bullet lists, bold, links, horizontal rule)
// rather than pull in a Markdown dependency.

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import privacyMd from '../content/legal/privacy-policy.md?raw';
import termsMd from '../content/legal/terms-of-service.md?raw';
import securityMd from '../content/legal/security.md?raw';
import BrandLogo from '../components/BrandLogo';

type LegalDoc = 'privacy' | 'terms' | 'security';

const SOURCES: Record<LegalDoc, string> = {
  privacy: privacyMd,
  terms: termsMd,
  security: securityMd,
};

// Render inline Markdown: **bold** and [text](url). Everything else is literal.
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined && match[3] !== undefined) {
      nodes.push(
        <a
          key={`${keyPrefix}-a${i}`}
          href={match[3]}
          className="text-navy underline hover:text-gold"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[2]}
        </a>
      );
    }
    lastIndex = pattern.lastIndex;
    i += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').trim();
    paragraph = [];
    if (!text) return;
    blocks.push(
      <p key={`p${key++}`} className="text-gray-700 leading-relaxed mb-4">
        {renderInline(text, `p${key}`)}
      </p>
    );
  };

  const flushList = () => {
    if (list.length === 0) return;
    const items = list;
    list = [];
    blocks.push(
      <ul key={`ul${key++}`} className="list-disc pl-6 mb-4 space-y-1 text-gray-700">
        {items.map((item, idx) => (
          <li key={idx} className="leading-relaxed">
            {renderInline(item, `li${key}-${idx}`)}
          </li>
        ))}
      </ul>
    );
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.trim() === '---') {
      flushParagraph();
      flushList();
      blocks.push(<hr key={`hr${key++}`} className="my-6 border-gray-200" />);
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h3 key={`h3${key++}`} className="text-lg font-semibold text-navy mt-6 mb-2">
          {renderInline(line.slice(4), `h3${key}`)}
        </h3>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h2 key={`h2${key++}`} className="text-xl font-bold text-navy mt-8 mb-3">
          {renderInline(line.slice(3), `h2${key}`)}
        </h2>
      );
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h1 key={`h1${key++}`} className="text-3xl font-bold text-navy mb-4">
          {renderInline(line.slice(2), `h1${key}`)}
        </h1>
      );
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      list.push(line.slice(2));
      continue;
    }
    // An indented line right after a bullet continues that bullet.
    if (list.length > 0 && paragraph.length === 0 && /^\s+\S/.test(line)) {
      list[list.length - 1] += ` ${line.trim()}`;
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}

export default function LegalPage({ doc }: { doc: LegalDoc }) {
  const blocks = React.useMemo(() => renderMarkdown(SOURCES[doc]), [doc]);

  return (
    <div className="standalone-screen">
      <header className="bg-navy text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <BrandLogo variant="white" className="h-10 w-10" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <article className="bg-white rounded-2xl shadow-sm p-6 sm:p-10">{blocks}</article>
        <div className="text-center mt-6 text-sm text-gray-500 flex flex-wrap justify-center gap-x-4 gap-y-1">
          {doc !== 'privacy' && (
            <Link to="/privacy" className="text-navy underline hover:text-gold">Privacy Policy</Link>
          )}
          {doc !== 'terms' && (
            <Link to="/terms" className="text-navy underline hover:text-gold">Terms of Service</Link>
          )}
          {doc !== 'security' && (
            <Link to="/security" className="text-navy underline hover:text-gold">Security</Link>
          )}
        </div>
      </main>
    </div>
  );
}
